// src/pages/Campaigns/EventPost/EventPostEditor.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Grid, Paper, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Chip, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  FormControlLabel, Radio, RadioGroup, Card, CardMedia, InputAdornment,
  Autocomplete, Tabs, Tab
} from '@mui/material';
import {
  Save as SaveIcon, Publish as PublishIcon, ArrowBack as BackIcon,
  Image as ImageIcon, VideoLibrary as VideoIcon, Close as CloseIcon,
  Delete as DeleteIcon, Schedule as ScheduleIcon, ViewModule as GalleryIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Node } from '@tiptap/core';

import TiptapEditor from '../../../components/Common/TiptapEditor';
import GalleryDialog from './GalleryDialog';
import axiosInstance from '../../../utils/axiosInstance';

const EventPostEditor = () => {
  const { chatbotId, postId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!postId;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    author_name: '',
    event_id: '',
    category_ids: []
  });

  const [categories, setCategories] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState(0);

  // Image upload dialog
  const [imageDialog, setImageDialog] = useState({ open: false, file: null, preview: null });
  const [imageSize, setImageSize] = useState('ORIGINAL');
  const [imageCaption, setImageCaption] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [isFeaturedImage, setIsFeaturedImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Video dialog
  const [videoDialog, setVideoDialog] = useState({ open: false });
  const [videoUrl, setVideoUrl] = useState('');
  const [videoCaption, setVideoCaption] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);

  // Schedule dialog
  const [scheduleDialog, setScheduleDialog] = useState({ open: false });
  const [scheduleDate, setScheduleDate] = useState('');

  // Featured image
  const [featuredImage, setFeaturedImage] = useState(null);

  // Media list
  const [media, setMedia] = useState([]);

  // Gallery dialog
  const [galleryDialog, setGalleryDialog] = useState({ open: false });
  const [galleries, setGalleries] = useState([]);

  // HTML Source mode
  const [isSourceMode, setIsSourceMode] = useState(false);
  const [sourceCode, setSourceCode] = useState('');

  // Custom Iframe extension for video embeds
  const Iframe = Node.create({
    name: 'iframe',
    group: 'block',
    atom: true,

    addAttributes() {
      return {
        src: {
          default: null,
        },
        width: {
          default: '100%',
        },
        height: {
          default: '400',
        },
        frameborder: {
          default: '0',
        },
        allowfullscreen: {
          default: 'true',
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'iframe',
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['iframe', HTMLAttributes];
    },

    addCommands() {
      return {
        setIframe: (options) => ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
      };
    },
  });

  // TiptapEditor setup
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Iframe,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '<p></p>',
    editorProps: {
      attributes: {
        class: 'tiptap-editor-area p-3 form-control',
      },
    },
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar({ ...snackbar, open: false }), 4000);
  };

  // Load categories and events
  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, eventsRes] = await Promise.all([
          axiosInstance.get('/api/event-post-categories', { params: { chatbot_id: chatbotId } }),
          axiosInstance.get('/api/event-posts/linked-events', { params: { chatbot_id: chatbotId } })
        ]);
        setCategories(catRes.data.categories || []);
        setLinkedEvents(eventsRes.data.events || []);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    if (chatbotId) loadData();
  }, [chatbotId]);

  // Load existing post if editing
  useEffect(() => {
    const loadPost = async () => {
      if (!isEditing) return;
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/api/event-posts/${postId}`, {
          params: { chatbot_id: chatbotId }
        });
        const post = response.data.post;

        setFormData({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          meta_keywords: post.meta_keywords || '',
          author_name: post.author_name || '',
          event_id: post.event_id || '',
          category_ids: post.categories?.map(c => c.id) || []
        });

        if (editor && post.content) {
          editor.commands.setContent(post.content);
        }

        if (post.featured_image_url) {
          setFeaturedImage({
            url: post.featured_image_url,
            alt: post.featured_image_alt
          });
        }

        setMedia(post.media || []);
        setGalleries(post.galleries || []);
      } catch (error) {
        showSnackbar('Error loading post', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [isEditing, postId, chatbotId, editor]);

  // Load galleries separately when post ID changes
  useEffect(() => {
    const loadGalleries = async () => {
      if (!postId) return;
      try {
        const response = await axiosInstance.get(`/api/event-posts/${postId}/galleries`, {
          params: { chatbot_id: chatbotId }
        });
        setGalleries(response.data.galleries || []);
      } catch (error) {
        console.error('Error loading galleries:', error);
      }
    };
    if (postId) loadGalleries();
  }, [postId, chatbotId]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      showSnackbar('Title is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        content: editor?.getHTML() || ''
      };

      let response;
      if (isEditing) {
        response = await axiosInstance.put(`/api/event-posts/${postId}`, payload, {
          params: { chatbot_id: chatbotId }
        });
      } else {
        response = await axiosInstance.post('/api/event-posts', payload, {
          params: { chatbot_id: chatbotId }
        });
      }

      const savedPostId = response.data.post?.id || postId;

      if (publish && savedPostId) {
        await axiosInstance.post(`/api/event-posts/${savedPostId}/publish`, null, {
          params: { chatbot_id: chatbotId }
        });
        showSnackbar('Post published successfully!', 'success');
      } else {
        showSnackbar('Post saved successfully!', 'success');
      }

      setTimeout(() => {
        navigate(`/chatbot/${chatbotId}/campaigns/event-posts`);
      }, 1000);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving post', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) {
      showSnackbar('Please select a date and time', 'error');
      return;
    }

    setSaving(true);
    try {
      // Save first if new post
      let savedPostId = postId;
      if (!isEditing) {
        const payload = {
          ...formData,
          content: editor?.getHTML() || ''
        };
        const response = await axiosInstance.post('/api/event-posts', payload, {
          params: { chatbot_id: chatbotId }
        });
        savedPostId = response.data.post?.id;
      }

      await axiosInstance.post(`/api/event-posts/${savedPostId}/schedule`, {
        scheduled_at: scheduleDate
      }, {
        params: { chatbot_id: chatbotId }
      });

      showSnackbar('Post scheduled successfully!', 'success');
      setScheduleDialog({ open: false });

      setTimeout(() => {
        navigate(`/chatbot/${chatbotId}/campaigns/event-posts`);
      }, 1000);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error scheduling post', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Image handling
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImageDialog({ open: true, file, preview: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const resizeImage = (file, sizeVariant) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        let scale = 1;
        switch (sizeVariant) {
          case 'SMALL': scale = 0.25; break;
          case 'MEDIUM': scale = 0.50; break;
          case 'LARGE': scale = 0.55; break;
          default: scale = 1;
        }

        const canvas = document.createElement('canvas');
        const newWidth = Math.round(img.width * scale);
        const newHeight = Math.round(img.height * scale);
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob((blob) => {
          resolve({
            blob,
            originalWidth: img.width,
            originalHeight: img.height,
            resizedWidth: newWidth,
            resizedHeight: newHeight
          });
        }, file.type, 0.9);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async () => {
    if (!imageDialog.file) {
      showSnackbar('Please select an image', 'error');
      return;
    }

    // For featured image, we need to save the post first
    if (isFeaturedImage && !postId) {
      showSnackbar('Please save the post first before setting featured image', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      const { blob, originalWidth, originalHeight, resizedWidth, resizedHeight } = await resizeImage(imageDialog.file, imageSize);

      // If it's featured image OR post is already saved, upload to server
      if (isFeaturedImage || postId) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', blob, imageDialog.file.name);
        formDataUpload.append('size_variant', imageSize);
        formDataUpload.append('original_width', originalWidth);
        formDataUpload.append('original_height', originalHeight);
        formDataUpload.append('resized_width', resizedWidth);
        formDataUpload.append('resized_height', resizedHeight);
        formDataUpload.append('is_featured', isFeaturedImage);
        formDataUpload.append('caption', imageCaption);
        formDataUpload.append('alt_text', imageAlt);

        const response = await axiosInstance.post(`/api/event-posts/${postId}/upload-image`, formDataUpload, {
          params: { chatbot_id: chatbotId },
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (isFeaturedImage) {
          setFeaturedImage({
            url: response.data.image_url,
            alt: imageAlt
          });
          showSnackbar('Featured image set successfully!', 'success');
        } else {
          // Insert image into editor
          if (editor && response.data.media?.url) {
            editor.chain().focus().setImage({ src: response.data.media.url, alt: imageAlt }).run();
          }
          showSnackbar('Image inserted successfully!', 'success');
        }
      } else {
        // For inline images before post is saved, convert to base64 and insert
        const reader = new FileReader();
        reader.onload = (e) => {
          if (editor) {
            editor.chain().focus().setImage({ src: e.target.result, alt: imageAlt }).run();
          }
          showSnackbar('Image inserted successfully! Note: Save the post to upload images to server.', 'info');
        };
        reader.readAsDataURL(blob);
      }

      setImageDialog({ open: false, file: null, preview: null });
      setImageSize('ORIGINAL');
      setImageCaption('');
      setImageAlt('');
      setIsFeaturedImage(false);
    } catch (error) {
      showSnackbar('Error uploading image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  // Video handling - Insert directly into content without saving to media gallery
  const handleAddVideo = () => {
    if (!videoUrl.trim()) {
      showSnackbar('Please enter a video URL', 'error');
      return;
    }

    if (!editor) {
      showSnackbar('Editor not ready', 'error');
      return;
    }

    setAddingVideo(true);
    try {
      // Parse video URL to get embed URL
      const embedUrl = parseVideoUrl(videoUrl);
      if (!embedUrl) {
        showSnackbar('Invalid video URL. Please use YouTube or Vimeo links.', 'error');
        setAddingVideo(false);
        return;
      }

      // Insert iframe directly into editor
      editor.commands.setIframe({
        src: embedUrl,
        width: '100%',
        height: '400',
        frameborder: '0',
        allowfullscreen: 'true',
      });

      showSnackbar('Video inserted successfully!', 'success');
      setVideoDialog({ open: false });
      setVideoUrl('');
      setVideoCaption('');
    } catch (error) {
      showSnackbar('Error inserting video', 'error');
    } finally {
      setAddingVideo(false);
    }
  };

  // Parse YouTube and Vimeo URLs to get embed URLs
  const parseVideoUrl = (url) => {
    // YouTube patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    // Vimeo pattern
    const vimeoPattern = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoPattern);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await axiosInstance.delete(`/api/event-posts/media/${mediaId}`, {
        params: { chatbot_id: chatbotId }
      });
      setMedia(media.filter(m => m.id !== mediaId));
      showSnackbar('Media deleted', 'success');
    } catch (error) {
      showSnackbar('Error deleting media', 'error');
    }
  };

  // Handle gallery insertion
  const handleInsertGallery = (shortcode, gallery) => {
    if (editor) {
      // Insert shortcode as a paragraph
      editor.chain()
        .focus()
        .insertContent(`<p>${shortcode}</p>`)
        .run();

      // Add to galleries list
      setGalleries(prev => [...prev, gallery]);
      showSnackbar('Gallery inserted into content', 'success');
    }
  };

  // Toggle HTML source mode
  const handleToggleSource = () => {
    if (!isSourceMode) {
      // Switching to source mode - get current HTML
      setSourceCode(editor?.getHTML() || '');
    } else {
      // Switching to visual mode - apply source code
      if (editor && sourceCode) {
        editor.commands.setContent(sourceCode);
      }
    }
    setIsSourceMode(!isSourceMode);
  };

  // Handle source code changes
  const handleSourceCodeChange = (event) => {
    setSourceCode(event.target.value);
  };

  // Apply source code and switch back to visual mode
  const handleApplySource = () => {
    if (editor && sourceCode) {
      editor.commands.setContent(sourceCode);
      setIsSourceMode(false);
      showSnackbar('HTML changes applied', 'success');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', mt:10 }}>
      {/* Snackbar */}
      {snackbar.open && (
        <Alert severity={snackbar.severity} sx={{ mb: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {isEditing ? 'Edit Post' : 'Create New Post'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setScheduleDialog({ open: true })}
            disabled={saving}
          >
            Schedule
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {saving ? 'Publishing...' : 'Publish'}
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TextField
              fullWidth
              label="Post Title"
              value={formData.title}
              onChange={handleChange('title')}
              variant="outlined"
              sx={{ mb: 3 }}
              placeholder="Enter an engaging title..."
            />

            {/* Tabs for Editor and Media */}
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label="Content" />
              <Tab label="Media" />
            </Tabs>

            {activeTab === 0 && (
              <>
                {/* Media Insert Buttons */}
                <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    component="label"
                    size="small"
                    disabled={isSourceMode}
                  >
                    Insert Image
                    <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<VideoIcon />}
                    onClick={() => setVideoDialog({ open: true })}
                    size="small"
                    disabled={isSourceMode}
                  >
                    Insert Video
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<GalleryIcon />}
                    onClick={() => setGalleryDialog({ open: true })}
                    size="small"
                    disabled={!postId || isSourceMode}
                    title={!postId ? 'Save post first to create galleries' : 'Create a gallery'}
                  >
                    Create Gallery
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant={isSourceMode ? 'contained' : 'outlined'}
                    startIcon={<CodeIcon />}
                    onClick={handleToggleSource}
                    size="small"
                    color={isSourceMode ? 'primary' : 'inherit'}
                  >
                    {isSourceMode ? 'Visual Mode' : 'HTML Source'}
                  </Button>
                </Stack>

                {/* Editor / Source Mode */}
                {isSourceMode ? (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        HTML Source Code
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleApplySource}
                      >
                        Apply & Return to Visual
                      </Button>
                    </Box>
                    <textarea
                      value={sourceCode}
                      onChange={handleSourceCodeChange}
                      style={{
                        width: '100%',
                        minHeight: '400px',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        padding: '16px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#1e1e1e',
                        color: '#d4d4d4',
                        resize: 'vertical'
                      }}
                      spellCheck={false}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Tip: Galleries use shortcodes like [gallery id="..." columns="3"]. Do not remove these.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                    <TiptapEditor editor={editor} height="400px" />
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>Uploaded Media</Typography>
                {media.length === 0 ? (
                  <Typography color="text.secondary">No media uploaded yet</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {media.map((item) => (
                      <Grid item xs={6} md={4} key={item.id}>
                        <Card>
                          {item.media_type === 'IMAGE' ? (
                            <CardMedia component="img" height="120" image={item.url} alt={item.alt_text} />
                          ) : (
                            <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                              <VideoIcon sx={{ fontSize: 40 }} />
                            </Box>
                          )}
                          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                              {item.media_type === 'IMAGE' ? item.size_variant : item.video_id}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => handleDeleteMedia(item.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <TextField
              fullWidth
              label="Excerpt"
              value={formData.excerpt}
              onChange={handleChange('excerpt')}
              multiline
              rows={3}
              placeholder="A brief summary of the post..."
              helperText="This will be displayed in post listings"
            />
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Featured Image */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Featured Image
            </Typography>
            {featuredImage ? (
              <Box sx={{ position: 'relative' }}>
                <img
                  src={featuredImage.url}
                  alt={featuredImage.alt}
                  style={{ width: '100%', borderRadius: 8 }}
                />
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'white' }}
                  onClick={() => setFeaturedImage(null)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                sx={{ py: 3 }}
              >
                Set Featured Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    setIsFeaturedImage(true);
                    handleImageSelect(e);
                  }}
                />
              </Button>
            )}
          </Paper>

          {/* Categories */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Categories
            </Typography>
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => option.name}
              value={categories.filter(c => formData.category_ids.includes(c.id))}
              onChange={(e, newValue) => {
                setFormData({ ...formData, category_ids: newValue.map(c => c.id) });
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select categories" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    sx={{ bgcolor: option.color, color: '#fff' }}
                  />
                ))
              }
            />
          </Paper>

          {/* Link to Event */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Link to Event
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Select Event (Optional)</InputLabel>
              <Select
                value={formData.event_id}
                onChange={handleChange('event_id')}
                label="Select Event (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {linkedEvents.map(event => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.name} ({event.event_start_date})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* SEO Settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              SEO Settings
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="URL Slug"
                value={formData.slug}
                onChange={handleChange('slug')}
                placeholder="auto-generated-from-title"
                helperText="Leave empty to auto-generate from title. Use lowercase with hyphens (e.g., my-event-post)"
              />
              <TextField
                fullWidth
                size="small"
                label="Meta Title"
                value={formData.meta_title}
                onChange={handleChange('meta_title')}
              />
              <TextField
                fullWidth
                size="small"
                label="Meta Description"
                value={formData.meta_description}
                onChange={handleChange('meta_description')}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                size="small"
                label="Keywords"
                value={formData.meta_keywords}
                onChange={handleChange('meta_keywords')}
                placeholder="keyword1, keyword2, keyword3"
              />
              <TextField
                fullWidth
                size="small"
                label="Author Name"
                value={formData.author_name}
                onChange={handleChange('author_name')}
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialog.open} onClose={() => setImageDialog({ open: false, file: null, preview: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Image
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setImageDialog({ open: false, file: null, preview: null })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {imageDialog.preview && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <img src={imageDialog.preview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }} />
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Image Size</Typography>
          <RadioGroup value={imageSize} onChange={(e) => setImageSize(e.target.value)} sx={{ mb: 2 }}>
            <FormControlLabel value="SMALL" control={<Radio />} label="Small (25% of original - 75% reduction)" />
            <FormControlLabel value="MEDIUM" control={<Radio />} label="Medium (50% of original - 50% reduction)" />
            <FormControlLabel value="LARGE" control={<Radio />} label="Large (55% of original - 45% reduction)" />
            <FormControlLabel value="ORIGINAL" control={<Radio />} label="Original (100% - no reduction)" />
          </RadioGroup>

          <TextField
            fullWidth
            size="small"
            label="Alt Text"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Describe the image for accessibility"
          />

          <TextField
            fullWidth
            size="small"
            label="Caption (Optional)"
            value={imageCaption}
            onChange={(e) => setImageCaption(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog({ open: false, file: null, preview: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleImageUpload} disabled={uploadingImage}>
            {uploadingImage ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Dialog */}
      <Dialog open={videoDialog.open} onClose={() => setVideoDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Video
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setVideoDialog({ open: false })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a YouTube or Vimeo URL to embed the video
          </Typography>
          <TextField
            fullWidth
            label="Video URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Caption (Optional)"
            value={videoCaption}
            onChange={(e) => setVideoCaption(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVideoDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleAddVideo} disabled={addingVideo}>
            {addingVideo ? 'Adding...' : 'Add Video'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog.open} onClose={() => setScheduleDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Post</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select when you want this post to be published
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            label="Publish Date & Time"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog({ open: false })}>Cancel</Button>
          <Button variant="contained" onClick={handleSchedule} disabled={saving}>
            {saving ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gallery Dialog */}
      <GalleryDialog
        open={galleryDialog.open}
        onClose={() => setGalleryDialog({ open: false })}
        onInsert={handleInsertGallery}
        postId={postId}
        axiosInstance={axiosInstance}
        chatbotId={chatbotId}
      />
    </Box>
  );
};

export default EventPostEditor;
